import { useEffect, useState } from 'react'
import { api } from '../api/client'
import { radius } from '../components/primitives/shape'
import { InstallCommand } from './InstallCommand'

/**
 * Dismissed per browser, not per account. Somebody who only reads the room
 * and never runs an agent says so once, here, and a second laptop asks again,
 * which is the right default for a machine that might be the one they code on.
 */
const DISMISSED = 'stagepost.no-machine-notice-dismissed'

function dismissedHere(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === '1'
  } catch {
    // Private browsing, or storage turned off: the notice shows, which is the
    // failure worth having.
    return false
  }
}

function dismissHere(): void {
  try {
    localStorage.setItem(DISMISSED, '1')
  } catch {
    // It comes back on the next load. Survivable.
  }
}

/**
 * One line above the room while no machine of this account is signed in.
 *
 * It asks the server again whenever the tab comes back into view, because the
 * way it goes away is somebody running `stagepost login` in a terminal and
 * approving it in another tab, and a notice that outlives the fix is one people
 * learn to ignore.
 */
export function NoMachineNotice({ onShowSteps }: { onShowSteps: () => void }) {
  const [unconnected, setUnconnected] = useState(false)
  const [dismissed, setDismissed] = useState(dismissedHere)

  useEffect(() => {
    if (dismissed) return
    // A check that fails shows nothing. This is a nudge, and telling someone
    // they have no machine because the server did not answer is a wrong one.
    const check = () => {
      api.tokens().then((tokens) => setUnconnected(tokens.length === 0), () => setUnconnected(false))
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    check()
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [dismissed])

  if (dismissed || !unconnected) return null

  return (
    <section
      aria-label="No machine signed in"
      style={{
        flex: 'none', display: 'flex', flexDirection: 'column', gap: 9,
        padding: '11px 16px 12px 20px', borderBottom: '1px solid var(--border)',
        background: 'var(--accent-wash)',
      }}
    >
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text)' }}>
        None of your machines is signed in, so your agents cannot reach this room.
        Install the command, then run <code style={{ font: '400 12px var(--font-machine)' }}>stagepost login</code>.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
        <div style={{ flex: '1 1 360px', minWidth: 0, maxWidth: 480 }}>
          <InstallCommand />
        </div>
        <button
          type="button"
          onClick={onShowSteps}
          style={{ padding: '7px 10px', borderRadius: radius.xs, background: 'none', cursor: 'pointer', font: '600 12px var(--font-interface)', color: 'var(--accent-bright)' }}
        >
          All steps
        </button>
        <button
          type="button"
          onClick={() => { dismissHere(); setDismissed(true) }}
          style={{ padding: '7px 10px', borderRadius: radius.xs, background: 'none', cursor: 'pointer', font: '600 12px var(--font-interface)', color: 'var(--text-dim)' }}
        >
          Not now
        </button>
      </div>
    </section>
  )
}
